/* eslint-disable no-nested-ternary */
import * as React from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from '../..';
import { showAlert, Store, useAuth } from '../../../context/authContext';
import { initSubscription, retryPayment } from '../../../context/storeContext';
import { paths } from '../../../routes';
import capitalize from '../../../utils/capitalize';
import env from '../../../utils/env';
import getAppURL from '../../../utils/getAppURL';
import { formatErrors } from '../../../utils/request';

type ModalProps = {
  notice: string;
  title: string;
  type:
    | 'INIT-SUBSCRIPTION'
    | 'SUCCESS-SUBSCRIPTION'
    | 'PAID-EXPIRED-SUBSCRIPTION'
    | 'TRIAL-EXPIRED-SUBSCRIPTION';
  store?: Store;
  plan?: string;
};

function SubscriptionPrompt({ notice, title, store, plan, type }: ModalProps) {
  const [, dispatch] = useAuth();
  const [status, setStatus] = React.useState<
    'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED'
  >('IDLE');

  const navigate = useNavigate();

  const reloadPage = () => {
    window.history.replaceState({}, document.title);
    window.location.reload();
  };

  const paymentDetails = store?.paymentPlan?.paymentDetails;

  const paymentDisplayInfo =
    paymentDetails?.channel === 'card'
      ? `${paymentDetails?.bank} ${capitalize(paymentDetails?.brand || '')} ${
          paymentDetails?.channel
        } ending with ${paymentDetails?.last4}`
      : paymentDetails?.channel === 'mobile_money'
      ? `${paymentDetails?.bank}  ${capitalize(
          paymentDetails?.channel?.replace('_', ' '),
        )} with number ${paymentDetails?.bin}${paymentDetails?.last4}`
      : 'None';

  const handleSubscription = async () => {
    if (store && plan) {
      try {
        setStatus('LOADING');

        const payload = {
          plan,
          storeId: store.id,
          callback_url: `${getAppURL()}${
            paths.VERIFY_SUBSCRIPTION_CALLBACK_URL_PATH
          }/`,
        };
        const response = (await initSubscription(payload)) as any;

        if (response?.transaction?.authorizationUrl) {
          Cookies.remove('_pos_possible_plan');
          window.location.replace(response?.transaction?.authorizationUrl);
        }
      } catch (error) {
        setStatus('REJECTED');

        let { message } = formatErrors(error);

        const supportLink = env('POS_SUPPORT_URL') || '#';

        message =
          message ||
          'Oops. Something unusual happened at our end. Please contact our support team!';

        showAlert(dispatch, {
          show: true,
          message: `${message}. [Get Help](${supportLink})`,
          type: 'ERROR',
        });
      }
    }
  };

  const handleRetryPayment = async () => {
    if (store && plan) {
      try {
        setStatus('LOADING');

        const payload = {
          plan,
          storeId: store.id,
          callback_url: `${getAppURL()}${
            paths.VERIFY_SUBSCRIPTION_CALLBACK_URL_PATH
          }/`,
        };

        const response = (await retryPayment(payload)) as any;

        if (response?.transaction?.verificationUrl) {
          window.location.replace(response?.transaction?.verificationUrl);
        }
      } catch (error) {
        setStatus('REJECTED');

        let { message } = formatErrors(error);

        const supportLink = env('POS_SUPPORT_URL') || '#';

        message =
          message ||
          'Oops. Something unusual happened at our end. Please contact our support team!';

        showAlert(dispatch, {
          show: true,
          message: `${message}. [Get Help](${supportLink})`,
          type: 'ERROR',
        });
      }
    }
  };

  // Your trial has expired
  return (
    <Modal show className="tablet:align-middle tablet:!w-[500px]">
      <div className="rounded-md bg-white">
        <div className="flex flex-col items-center px-5 py-5">
          <h3
            className="text-pos-black text-2xl tablet:text-3xl font-semibold mb-2
              inline-flex text-center tablet:leading-[2.5rem]"
          >
            {title}
          </h3>

          <div className="text-pos-black text-center text-base mb-2 font-normal space-y-4">
            <p>{notice}</p>
          </div>

          {paymentDetails && (
            <div className="flex text-pos-black text-left mt-3 mb-2 items-end w-full">
              <span
                className="inline-flex flex-col rounded-[4px] text-xbase pl-2 pr-1 py-2 font-light 
                bg-[#ddf4ff] text-pos-blue leading-normal w-full"
              >
                <span className="text-sm font-medium w-full inline-block">
                  Payment Details:{' '}
                </span>
                {paymentDisplayInfo}
              </span>
            </div>
          )}
        </div>

        {type === 'INIT-SUBSCRIPTION' && (
          <div className="w-full text-right flex flex-row justify-center tablet:justify-end ml-auto space-x-4 bg-pos-yellow px-5 py-7 rounded-b-md">
            <Button
              label="Cancel"
              onClick={() => {
                Cookies.remove('_pos_possible_plan');
                reloadPage();
              }}
              type="button"
              className="!bg-transparent border-2 border-pos-black !min-w-[100px]  h-[40px]
                      items-center justify-center !text-pos-black text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
            />
            <Button
              label="Subscribe"
              onClick={handleSubscription}
              type="button"
              disabled={status === 'LOADING'}
              loading={status === 'LOADING'}
              className="!bg-pos-black border border-[rgba(27,31,36,0.15)] !min-w-[150px]  h-[40px]
                      items-center justify-center !text-pos-yellow text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
            />
          </div>
        )}

        {type === 'SUCCESS-SUBSCRIPTION' && (
          <div className="w-full text-right flex flex-row justify-center ml-auto space-x-4 bg-pos-yellow px-5 py-7 rounded-b-md">
            <Button
              label="Return home"
              onClick={reloadPage}
              type="button"
              className="!bg-pos-black border border-[rgba(27,31,36,0.15)] !min-w-[150px]  h-[40px]
              items-center justify-center !text-pos-yellow text-xbase
              shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
            />
          </div>
        )}

        {(type === 'TRIAL-EXPIRED-SUBSCRIPTION' ||
          type === 'PAID-EXPIRED-SUBSCRIPTION') && (
          <div className="w-full text-right flex flex-row justify-center tablet:justify-end ml-auto space-x-4 bg-pos-yellow px-5 py-7 rounded-b-md">
            <Button
              label="Get help"
              onClick={() => {
                navigate(
                  `${paths.LANDING_SUPPORT_URL_PATH}?store=${store?.name}`,
                );
              }}
              type="button"
              className="!bg-transparent border-2 border-pos-black !min-w-[100px]  h-[40px]
                      items-center justify-center !text-pos-black text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
            />

            <Button
              label={
                type === 'TRIAL-EXPIRED-SUBSCRIPTION'
                  ? 'Subscribe'
                  : 'Retry payment'
              }
              onClick={() => {
                if (type === 'TRIAL-EXPIRED-SUBSCRIPTION') {
                  navigate(paths.UPGRADE_SUBSCRIPTION_URL_PATH);
                  return;
                }

                if (paymentDetails?.reusable) {
                  handleRetryPayment();
                  return;
                }

                handleSubscription();
              }}
              type="button"
              disabled={status === 'LOADING'}
              loading={status === 'LOADING'}
              className="!bg-pos-black border border-[rgba(27,31,36,0.15)] !min-w-[150px]  h-[40px]
                      items-center justify-center !text-pos-yellow text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

SubscriptionPrompt.defaultProps = {
  store: undefined,
  plan: undefined,
};

export default SubscriptionPrompt;
